import { ArrowLeft, ArrowRight, Bookmark, Globe, Home, RefreshCw, Search, Shield, ShieldAlert, ShieldCheck, ShieldQuestion, Square } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '@/OS/lib/utils';
import { BrowserSecurityDialog } from './BrowserSecurityDialog';
import { BrowserSettingsDialog } from './BrowserSettingsDialog';
import type { BrowserPageTheme, BrowserTabSecurityState, BrowserTabState } from './types';

type BrowserToolbarProps = {
  activeTab: BrowserTabState | null;
  addressValue: string;
  browserTheme: BrowserPageTheme;
  homepage: string;
  isSettingsOpen: boolean;
  isSecurityDetailsOpen: boolean;
  isPrivateMode: boolean;
  isCurrentPageBookmarked: boolean;
  portalContainer?: HTMLElement | null;
  onAddressChange: (value: string) => void;
  onThemeChange: (theme: BrowserPageTheme) => void | Promise<void>;
  onHomepageChange: (homepage: string) => void | Promise<void>;
  onSettingsOpenChange: (open: boolean) => void;
  onSecurityDetailsOpenChange: (open: boolean) => void;
  onSubmit: () => void;
  onBack: () => void;
  onForward: () => void;
  onReload: () => void;
  onStop: () => void;
  onHome: () => void;
  onOpenLibrary: () => void;
  onOpenPasswordManager: () => void;
  onToggleBookmark: () => void;
  onTogglePrivateMode: () => void;
};

const getSecurityButtonTone = (security: BrowserTabSecurityState) => {
  if (security.level === 'secure') {
    return {
      icon: ShieldCheck,
      label: security.label,
      className: 'text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300',
    };
  }

  if (security.level === 'insecure') {
    return {
      icon: ShieldAlert,
      label: security.label,
      className: 'text-destructive hover:text-destructive',
    };
  }

  if (security.level === 'unknown') {
    return {
      icon: ShieldQuestion,
      label: security.label,
      className: 'text-primary hover:text-primary',
    };
  }

  return {
    icon: Globe,
    label: security.label,
    className: 'text-muted-foreground hover:text-foreground',
  };
};

export const BrowserToolbar = ({
  activeTab,
  addressValue,
  browserTheme,
  homepage,
  isSettingsOpen,
  isSecurityDetailsOpen,
  isPrivateMode,
  isCurrentPageBookmarked,
  portalContainer,
  onAddressChange,
  onThemeChange,
  onHomepageChange,
  onSettingsOpenChange,
  onSecurityDetailsOpenChange,
  onSubmit,
  onBack,
  onForward,
  onReload,
  onStop,
  onHome,
  onOpenLibrary,
  onOpenPasswordManager,
  onToggleBookmark,
  onTogglePrivateMode,
}: BrowserToolbarProps) => {
  const security = activeTab?.security || {
    level: 'neutral',
    label: 'Page info',
    summary: 'No page is currently open.',
    origin: null,
    certificate: null,
  };
  const securityTone = getSecurityButtonTone(security);
  const SecurityIcon = securityTone.icon;

  return (
    <form
      className="flex items-center gap-2 border-b border-border/70 bg-background px-3 py-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <Button type="button" size="icon" variant="outline" onClick={onBack} disabled={!activeTab?.canGoBack}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <Button type="button" size="icon" variant="outline" onClick={onForward} disabled={!activeTab?.canGoForward}>
        <ArrowRight className="h-4 w-4" />
      </Button>
      <Button type="button" size="icon" variant="outline" onClick={activeTab?.isLoading ? onStop : onReload}>
        {activeTab?.isLoading ? <Square className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
      </Button>
      <Button type="button" size="icon" variant="outline" onClick={onHome}>
        <Home className="h-4 w-4" />
      </Button>

      <div className="flex min-w-0 flex-1 items-center rounded-md border border-input bg-background">
        <button
          type="button"
          onClick={() => onSecurityDetailsOpenChange(true)}
          disabled={!activeTab}
          className={cn(
            'ml-1 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            securityTone.className
          )}
          aria-label={`Open site security details: ${security.label}`}
        >
          <SecurityIcon className="h-4 w-4" />
          <span className="max-w-28 truncate">{securityTone.label}</span>
        </button>

        <div className="relative min-w-0 flex-1">
          <Input
            value={addressValue}
            onChange={(event) => onAddressChange(event.target.value)}
            onFocus={(event) => event.currentTarget.select()}
            onClick={(event) => event.currentTarget.select()}
            className="border-0 bg-transparent pl-2 pr-10 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            placeholder="Search Navigator or enter a URL"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      <Button type="submit">Go</Button>

      <Button
        type="button"
        size="icon"
        variant={isCurrentPageBookmarked ? 'default' : 'outline'}
        onClick={onToggleBookmark}
        aria-label={isCurrentPageBookmarked ? 'Remove bookmark' : 'Add bookmark'}
      >
        <Bookmark className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        size="icon"
        variant={isPrivateMode ? 'default' : 'outline'}
        onClick={onTogglePrivateMode}
        aria-label={isPrivateMode ? 'Disable private mode' : 'Enable private mode'}
      >
        <Shield className="h-4 w-4" />
      </Button>

      <BrowserSettingsDialog
        open={isSettingsOpen}
        browserTheme={browserTheme}
        homepage={homepage}
        portalContainer={portalContainer}
        onOpenChange={onSettingsOpenChange}
        onThemeChange={onThemeChange}
        onHomepageChange={onHomepageChange}
        onOpenLibrary={onOpenLibrary}
        onOpenPasswordManager={onOpenPasswordManager}
      />

      <BrowserSecurityDialog
        open={isSecurityDetailsOpen}
        onOpenChange={onSecurityDetailsOpenChange}
        security={security}
        url={activeTab?.url || 'about:blank'}
        portalContainer={portalContainer}
      />
    </form>
  );
};